import { useState } from "react"
import type { BuildSummaryCardData } from "../types"
import styles from "../styles/widget.module.css"
import { BuildHero } from "./BuildStepCard"
import { ContinueShoppingChips } from "./ContinueShoppingChips"

function fmtPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

function fmtDollars(dollars: number): string {
  return `$${dollars.toFixed(2)}`
}

export function BuildSummaryCard({
  data,
  onAddToCart,
  onUserReply,
}: {
  data: BuildSummaryCardData
  /** Add to cart. Receives the kit's Shopify variant id (added DIRECTLY to
   *  the Shopify cart by the caller so the cart opens instantly) plus the
   *  names of any selected add-ons (which are product ids needing variant
   *  resolution, so the caller routes those through Pearl). */
  onAddToCart: (kitVariantId: number, addOnNames: string[]) => void
  /** Fires when the customer taps a "carry on shopping" chip — sends the
   *  chip text to Pearl as a user message so the canvas pivots to the
   *  next surface (more colors / kits / etc). */
  onUserReply: (text: string) => void
}) {
  const showCompareAt =
    data.compareAtPrice != null && data.compareAtPrice > data.price
  const upsells = data.upsellProducts ?? []
  // Track selected upsells by handle so re-renders preserve state.
  const [selected, setSelected] = useState<Set<string>>(() => new Set())

  function toggle(handle: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(handle)) next.delete(handle)
      else next.add(handle)
      return next
    })
  }

  const selectedCount = selected.size
  const ctaLabel =
    selectedCount === 0
      ? "Add to cart"
      : `Add ${selectedCount + 1} items to cart`

  function handleConfirm() {
    // The kit's variant id is added straight to the Shopify cart by the
    // caller (+ opens the cart). Selected add-ons are merchant product ids
    // that still need Pearl to resolve the right variant, so we hand their
    // names back for the caller to route through Pearl.
    const addOnNames = upsells
      .filter((u) => selected.has(u.handle))
      .map((u) => u.name)
    onAddToCart(data.variantId, addOnNames)
  }

  // Prefer matte (OEM-like) over gloss. Backend falls back to product
  // main image when no speed-shape is set, so this is the only logic
  // we need here.
  const heroSrc = data.colorRenders?.matte ?? data.colorRenders?.gloss

  return (
    <div className={styles.rc}>
      {heroSrc && <BuildHero src={heroSrc} />}
      <div className={styles["rc-body"]}>
        <div className={styles["rc-sub"]}>Your kit is ready</div>
        <div className={styles["rc-title"]}>{data.variantTitle}</div>
        {data.activeTags.length > 0 && (
          <div className={styles["rc-sub"]} style={{ marginTop: 4 }}>
            {data.activeTags.join(" · ")}
          </div>
        )}
        <div className={styles["rc-price-row"]} style={{ marginTop: 8 }}>
          <span className={styles["rc-price"]}>{fmtPrice(data.price)}</span>
          {showCompareAt && (
            <span className={styles["rc-price-compare"]}>{fmtPrice(data.compareAtPrice!)}</span>
          )}
        </div>
        {data.kitContents.length > 0 && (
          <div
            style={{
              margin: "12px 0 4px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "8px 10px",
            }}
          >
            {data.kitContents.map((c, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12,
                  lineHeight: 1.3,
                  padding: "6px 8px",
                  borderRadius: 10,
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  minWidth: 0,
                }}
              >
                {c.imageUrl ? (
                  <img
                    src={c.imageUrl}
                    alt=""
                    loading="lazy"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 7,
                      objectFit: "cover",
                      flexShrink: 0,
                      background: "rgba(255,255,255,0.08)",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 7,
                      background: "rgba(255,255,255,0.08)",
                      flexShrink: 0,
                    }}
                  />
                )}
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={c.name}
                >
                  {c.name}
                  {c.quantity > 1 && (
                    <span style={{ opacity: 0.65 }}> × {c.quantity}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
        {upsells.length > 0 && (
          <div
            style={{
              marginTop: 14,
              padding: "10px 12px",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 12,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.7, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Add-ons
            </div>
            {upsells.map((u) => {
              const isSelected = selected.has(u.handle)
              return (
                <div
                  key={u.handle}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 0",
                    fontSize: 12.5,
                    borderTop: "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  {/* Square product thumbnail to mirror the kit-contents
                    * rows above — gives the add-on the same shopping-cart
                    * "product line" feel rather than a bare text row. */}
                  {u.imageUrl ? (
                    <img
                      src={u.imageUrl}
                      alt=""
                      loading="lazy"
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 8,
                        objectFit: "cover",
                        flexShrink: 0,
                        background: "rgba(255,255,255,0.06)",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 8,
                        background: "rgba(255,255,255,0.08)",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {u.name}
                    </div>
                    <div style={{ opacity: 0.7, fontSize: 11.5, marginTop: 1 }}>{fmtDollars(u.price)}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggle(u.handle)}
                    aria-pressed={isSelected}
                    style={{
                      padding: "5px 14px",
                      borderRadius: 999,
                      border: "1px solid",
                      borderColor: isSelected ? "transparent" : "rgba(255,255,255,0.22)",
                      background: isSelected ? "#fff" : "transparent",
                      color: isSelected ? "#0d152c" : "inherit",
                      fontSize: 11.5,
                      fontWeight: 600,
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    {isSelected ? "Added" : "Add"}
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>
      {/* Sticky CTA: pinned to the bottom of the scrolling canvas-body
       *  via the wrapping <div data-build-summary-cta /> in CanvasShell
       *  (negative margins + linear-gradient fade let it bleed past the
       *  body padding without a hard line). Keeps the customer one tap
       *  from adding to cart no matter how long the kit-contents grid
       *  scrolls. */}
      <div data-build-summary-cta>
        {/* Inline overrides on the rc-cta button — the classic widget's
         *  CTA styles are tuned for a tiny inline pill, way too small for
         *  a primary basket commit on a dark canvas. Force a tall white
         *  button with bold dark text and the same shape as the basket's
         *  Checkout CTA so they read as the same family of "go" action. */}
        <button
          type="button"
          className={`${styles["rc-cta"]} ${styles["rc-cta-build"]}`}
          onClick={handleConfirm}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            padding: "11px 14px",
            borderRadius: 11,
            // color + animation come from .rc-cta-build (brand blue / white).
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "-0.005em",
            margin: 0,
            border: "none",
            cursor: "pointer",
          }}
        >
          {ctaLabel}
        </button>
        {/* Continue-shopping chips — outline pills sitting just below
         *  the primary CTA. The customer might want to keep browsing
         *  before committing; these give them a one-tap pivot to another
         *  surface without having to type a message. */}
        <ContinueShoppingChips
          onSelect={onUserReply}
          chips={[
            "Show me more colors",
            "Browse more kits",
            "What add-ons should I get?",
          ]}
        />
      </div>
    </div>
  )
}
